'use client';

import React, { useRef, useEffect, useState } from 'react';
import Link from 'next/link';

const stats = [
    { value: '2', suffix: '+', label: 'Flagship products', color: 'var(--primary)' },
    { value: '4', suffix: '+', label: 'Years of engineering', color: 'var(--accent)' },
    { value: '2', suffix: ' fields', label: 'EdTech & DevTools', color: '#F59E0B' },
    { value: '1', suffix: ' vision', label: 'Technology for progress', color: 'var(--primary)' },
];

const recognitions = [
    { title: 'Wisly — Education Technology Platform', sub: 'Students · Parents · Tutors · Institutions' },
    { title: 'CapState — Developer Version Control', sub: 'Granular restore · Team collaboration' },
    { title: 'Custom Technology Solutions', sub: 'Enterprise · Startups · Organizations' },
    { title: 'Digital Infrastructure Consulting', sub: 'Architecture · Systems design · Scale' },
];

export default function CredibilitySection() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);
    const [counts, setCounts] = useState(stats?.map(() => 0));

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    stats.forEach((stat, i) => {
                        const target = parseInt(stat.value);
                        let current = 0;
                        const step = Math.ceil(target / 20);
                        const interval = setInterval(() => {
                            current = Math.min(current + step, target);
                            setCounts((prev) => {
                                const next = [...prev];
                                next[i] = current;
                                return next;
                            });
                            if (current >= target) clearInterval(interval);
                        }, 60);
                    });
                }
            },
            { threshold: 0.2 }
        );
        if (sectionRef?.current) observer?.observe(sectionRef?.current);
        return () => observer?.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className="py-24 px-6 md:px-10 border-t border-border opacity-100">
            <div className="max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-12 gap-16">
                    {/* Left: Narrative */}
                    <div className="lg:col-span-5 flex flex-col justify-between gap-10">
                        <div>
                            <span className="section-label text-primary mb-3 block">About Codigo</span>
                            <h2 className="text-section-title text-foreground mb-6">
                                A technology company built for the long game.
                            </h2>
                            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                                Codigo operates at the intersection of software engineering, education, productivity, and digital infrastructure. We don't build software for the sake of technology — we build systems designed around how people and organizations actually work, learn, and grow.
                            </p>
                            <p className="text-sm text-muted-foreground leading-relaxed mb-8">
                                A modern African technology company with global ambitions, Codigo's product philosophy centers on intelligence, reliability, and long-term thinking.
                            </p>
                            <Link href="/contact" className="link-arrow text-sm font-700 text-foreground hover:text-primary transition-colors">
                                Work with Codigo
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Link>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-4">
                            {stats?.map((stat, i) => (
                                <div key={stat?.label} className="glass-card p-5 rounded-2xl">
                                    <p className="stat-number mb-1" style={{ color: stat?.color }}>
                                        {counts?.[i]}{stat?.suffix}
                                    </p>
                                    <p className="text-xs text-muted-foreground leading-snug">{stat?.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: What we do list */}
                    <div className="lg:col-span-7">
                        <p className="section-label text-muted-foreground mb-8">What we build</p>
                        <div className="divide-y divide-border">
                            {recognitions?.map((item, index) => (
                                <div
                                    key={item?.title}
                                    className={`py-7 flex justify-between items-center group cursor-pointer transition-all duration-300
                    ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                                    style={{ transitionDelay: `${index * 100}ms` }}
                                >
                                    <div className="space-y-1.5">
                                        <h4 className="text-lg font-700 text-foreground group-hover:text-primary transition-colors duration-300 tracking-tight">
                                            {item?.title}
                                        </h4>
                                        <p className="section-label text-muted-foreground">{item?.sub}</p>
                                    </div>
                                    <div className="w-10 h-10 rounded-xl glass-card flex items-center justify-center text-muted-foreground group-hover:text-primary group-hover:bg-primary/10 transition-all duration-300">
                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                            <path d="M4 14L14 4M14 4H7M14 4v7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}